import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "./api";

type SessionState = { status: "loading" } | { status: "signed_out" } | { status: "signed_in"; session: Session };

const SessionContext = createContext<SessionState>({ status: "loading" });

/** Holds the Supabase session; every page reads auth state from here, never from storage directly. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const queryClient = useQueryClient();
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    void supabase.auth.getSession().then(async ({ data }) => {
      if (data.session) {
        // Validate session with the server. If the token is invalid or from a different project, clear it.
        const { error } = await supabase.auth.getUser();
        if (error) {
          await supabase.auth.signOut();
          currentUserIdRef.current = null;
          setState({ status: "signed_out" });
          return;
        }
        currentUserIdRef.current = data.session.user.id;
        setState({ status: "signed_in", session: data.session });
      } else {
        currentUserIdRef.current = null;
        setState({ status: "signed_out" });
      }
    });

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id ?? null;
      const previousUserId = currentUserIdRef.current;

      if (event === "SIGNED_OUT") {
        currentUserIdRef.current = null;
        queryClient.clear();
        setState({ status: "signed_out" });
      } else if (event === "SIGNED_IN") {
        // Only wipe the query cache if a different user has signed in
        if (newUserId && previousUserId && newUserId !== previousUserId) {
          queryClient.clear();
        }
        currentUserIdRef.current = newUserId;
        if (session) {
          setState((prev) => {
            if (prev.status === "signed_in" && prev.session.access_token === session.access_token) {
              return prev;
            }
            return { status: "signed_in", session };
          });
        }
      } else if (session) {
        currentUserIdRef.current = newUserId;
        setState((prev) => {
          if (prev.status === "signed_in" && prev.session.access_token === session.access_token) {
            return prev;
          }
          return { status: "signed_in", session };
        });
      } else {
        currentUserIdRef.current = null;
        setState({ status: "signed_out" });
      }
    });

    return () => data.subscription.unsubscribe();
  }, [queryClient]);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
